import { documentUrl } from '@/hooks/useInbox'
import { useT } from '@/i18n/useT'
import { formatAmount } from '@/utils/inboxFormat'
import { Icon, Pill } from '@/components/ui'
import type { InboxItemRef } from '@/types/inbox'

/**
 * Items of one mail as a compact list with a link to each PDF. Shared by the
 * inbox drawer (enclosures of a main invoice, or main invoices of an
 * enclosure) and the Database preview.
 */
export function EnclosureList({
  items,
  showState = false,
}: {
  items: InboxItemRef[]
  /** Mark enclosures that are already confirmed (state dismissed) */
  showState?: boolean
}) {
  const { t } = useT()

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {items.map((ref) => {
        const title = ref.supplierName || ref.filename || t('Document')
        const meta = [
          ref.invoiceNumber,
          ref.totalGross != null ? formatAmount(ref.totalGross, ref.currency) : null,
        ]
          .filter(Boolean)
          .join(' · ')
        return (
          <div
            key={ref.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
              background: 'var(--field)',
              border: '1px solid var(--line2)',
              borderRadius: 8,
              padding: '9px 12px',
            }}
          >
            <div style={{ flex: 1, minWidth: 140 }}>
              <div style={{ fontSize: 13, fontWeight: 700, overflowWrap: 'anywhere' }}>{title}</div>
              <div style={{ fontSize: 11, color: 'var(--faint)', overflowWrap: 'anywhere' }}>
                {[ref.filename, meta].filter(Boolean).join(' · ') || '–'}
              </div>
            </div>
            {showState && ref.state === 'dismissed' && (
              <Pill>{t('Filed as enclosure')}</Pill>
            )}
            {ref.documentId ? (
              <a
                className="fx-btn fx-btn--outline fx-btn--sm"
                href={documentUrl(ref.documentId, true)}
                target="_blank"
                rel="noopener"
              >
                <Icon name="external" size={14} />
                {t('Open document')}
              </a>
            ) : (
              <span style={{ fontSize: 11, color: 'var(--faint)' }}>{t('No attachment')}</span>
            )}
          </div>
        )
      })}
    </div>
  )
}
